import {timezoneSafeTimestamp} from '../core/time.js';

export const LiveEventType=Object.freeze({
  WOOD_ADDED:'wood_added',
  CHAMBER_OPENED:'chamber_opened',
  STAGE_CHANGED:'stage_changed',
  PRODUCT_CHECKED:'product_checked',
  CUSTOM:'custom'
});

const TYPES=new Set(Object.values(LiveEventType));

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optional(value,label,max=160){
  if(value==null||value==='')return null;
  return required(value,label,max);
}

function timestamp(value,label){
  return timezoneSafeTimestamp(value,label);
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return required(id,'id',128);
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function eventType(value){
  const key=String(value||'').trim().toLowerCase();
  if(!TYPES.has(key))throw new TypeError('Unsupported Live event type');
  return key;
}

export function createLiveEvent({
  id,
  userId,
  sessionId,
  eventType:type,
  occurredAt,
  note=null,
  customText=null,
  stageId=null,
  itemId=null,
  woodId=null,
  stageMutationBatchId=null
}={}){
  const kind=eventType(type);
  const custom=optional(customText,'customText',160);
  if(kind===LiveEventType.CUSTOM&&!custom)throw new TypeError('customText is required for a custom event');
  if(kind!==LiveEventType.CUSTOM&&custom)throw new TypeError('customText is only allowed for a custom event');

  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    eventType:kind,
    occurredAt:timestamp(occurredAt,'occurredAt'),
    note:optional(note,'note',500),
    customText:custom,
    stageId:optional(stageId,'stageId',128),
    itemId:optional(itemId,'itemId',128),
    woodId:optional(woodId,'woodId',128),
    stageMutationBatchId:optional(stageMutationBatchId,'stageMutationBatchId',128)
  });
}

export function liveEventToRow(event){
  const value=createLiveEvent(event);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    event_type:value.eventType,
    occurred_at:value.occurredAt,
    note:value.note,
    custom_text:value.customText,
    stage_id:value.stageId,
    item_id:value.itemId,
    wood_id:value.woodId,
    mutation_batch_id:value.stageMutationBatchId
  });
}

export function liveEventFromRow(row={}){
  return createLiveEvent({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    eventType:row.event_type,
    occurredAt:row.occurred_at,
    note:row.note,
    customText:row.custom_text,
    stageId:row.stage_id,
    itemId:row.item_id,
    woodId:row.wood_id,
    stageMutationBatchId:row.mutation_batch_id
  });
}

export function orderLiveEvents(events=[]){
  if(!Array.isArray(events))throw new TypeError('events must be an array');
  return Object.freeze([...events]
    .map(createLiveEvent)
    .sort((a,b)=>Date.parse(a.occurredAt)-Date.parse(b.occurredAt)||String(a.id).localeCompare(String(b.id))));
}
